import React, { useState, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Upload, Trash2, AlertCircle, CheckCircle, FileUp, X, Search, Pencil, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Cast helper for tables not yet in generated types
const fromAny = (table: string) => (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> }).from(table);
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProductMappingFiles,
  useDeactivateProductMappingFile,
  useAllProductMappings,
  useUpdateProductMapping,
  useSoftDeleteProductMapping,
  useProductMappingHistory,
  type ProductMappingFile,
  type ProductMapping,
} from '@/hooks/useProductMappings';

const IDENTIFIER_TYPES = ['SKU', 'UPC', 'ASIN', 'EAN', 'GTIN'] as const;
type ColumnType = (typeof IDENTIFIER_TYPES)[number] | 'Metadata' | 'Ignore';

function isIdentifierType(type: string): boolean {
  return (IDENTIFIER_TYPES as readonly string[]).includes(type);
}

interface ColumnMapping {
  column_header: string;
  column_type: ColumnType;
  /** Custom label for Metadata columns — defaults to the CSV column header */
  metadata_label?: string;
}

interface ProductMappingsManagerProps {
  organisationId: string;
  productIdentifier: string;
}

function parseCSVLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}

function detectDelimiter(headerLine: string): string {
  const commaCount = (headerLine.match(/,/g) || []).length;
  const semicolonCount = (headerLine.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
}

export const ProductMappingsManager: React.FC<ProductMappingsManagerProps> = ({
  organisationId,
  productIdentifier,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Search state for mappings preview
  const [mappingSearch, setMappingSearch] = useState('');

  // Edit mapping modal state
  const [editingMapping, setEditingMapping] = useState<ProductMapping | null>(null);
  const [editForm, setEditForm] = useState({
    primary_value: '',
    primary_type: '',
    alias_type: '',
    alias_value: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  // History dialog state
  const [historyMapping, setHistoryMapping] = useState<{ aliasType: string; aliasValue: string } | null>(null);

  // File deactivation confirmation state
  const [fileToDeactivate, setFileToDeactivate] = useState<{ id: string; filename: string } | null>(null);

  // Hooks
  const { data: files, isLoading: filesLoading } = useProductMappingFiles(organisationId);
  const { data: allMappings, isLoading: mappingsLoading } = useAllProductMappings(organisationId);
  const deactivateFileMutation = useDeactivateProductMappingFile();
  const updateMappingMutation = useUpdateProductMapping();
  const softDeleteMappingMutation = useSoftDeleteProductMapping();
  const { data: historyRows, isLoading: historyLoading } = useProductMappingHistory(
    organisationId,
    historyMapping?.aliasType,
    historyMapping?.aliasValue
  );

  // Build filename lookup from files
  const fileNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (files) {
      for (const f of files) {
        map.set(f.id, f.filename);
      }
    }
    return map;
  }, [files]);

  // Filter and group mappings
  const filteredMappings = useMemo(() => {
    if (!allMappings) return [];
    const search = mappingSearch.toLowerCase().trim();
    if (!search) return allMappings;
    return allMappings.filter(
      (m) =>
        m.primary_value.toLowerCase().includes(search) ||
        m.alias_value.toLowerCase().includes(search)
    );
  }, [allMappings, mappingSearch]);

  const resetState = () => {
    setCsvHeaders([]);
    setCsvRows([]);
    setColumnMappings([]);
    setFileName(null);
    setParseError(null);
    setValidationError(null);
    setShowPreview(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError(null);
    setValidationError(null);
    setShowPreview(false);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');

        if (lines.length < 2) {
          setParseError('CSV file must contain a header row and at least one data row.');
          return;
        }

        const delimiter = detectDelimiter(lines[0]);
        const headers = parseCSVLine(lines[0], delimiter);

        if (headers.length === 0 || headers.every((h) => h === '')) {
          setParseError('Could not detect any column headers in the CSV.');
          return;
        }

        const dataRows: string[][] = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVLine(lines[i], delimiter);
          if (cols.some((c) => c !== '')) {
            dataRows.push(cols);
          }
        }

        if (dataRows.length === 0) {
          setParseError('No data rows found in the CSV.');
          return;
        }

        setCsvHeaders(headers);
        setCsvRows(dataRows);

        // Auto-map columns: try to match header names to identifier types
        const mappings: ColumnMapping[] = headers.map((header) => {
          const upper = header.toUpperCase().trim();
          const matchedIdentifier = IDENTIFIER_TYPES.find((t) => upper === t);
          if (matchedIdentifier) {
            return { column_header: header, column_type: matchedIdentifier };
          }
          return { column_header: header, column_type: 'Ignore' as ColumnType, metadata_label: header };
        });
        setColumnMappings(mappings);
      } catch {
        setParseError('Failed to parse CSV file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const handleColumnMappingChange = (index: number, value: ColumnType) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        column_type: value,
        metadata_label: value === 'Metadata'
          ? (updated[index].metadata_label || updated[index].column_header)
          : undefined,
      };
      return updated;
    });
    setValidationError(null);
  };

  const handleMetadataLabelChange = (index: number, label: string) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], metadata_label: label };
      return updated;
    });
  };

  const validateAndPreview = () => {
    // Check: at least one column mapped to primaryIdentifier
    const primaryColumns = columnMappings.filter(
      (m) => m.column_type === productIdentifier
    );
    if (primaryColumns.length < 1) {
      setValidationError(
        `Your primary identifier (${productIdentifier}) must be mapped to at least one column before importing.`
      );
      return;
    }

    // Check: no non-primary identifier type is mapped to more than one column
    const identifierColumns = columnMappings.filter(
      (m) => isIdentifierType(m.column_type) && m.column_type !== productIdentifier
    );
    const typeCounts = identifierColumns.reduce((acc, m) => {
      acc[m.column_type] = (acc[m.column_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const duplicateTypes = Object.entries(typeCounts).filter(([, count]) => count > 1);
    if (duplicateTypes.length > 0) {
      const dupType = duplicateTypes[0][0];
      setValidationError(`${dupType} is mapped to multiple columns. Each identifier type can only be used once.`);
      return;
    }

    // Check: metadata columns must have a non-empty label
    const metaColumns = columnMappings.filter((m) => m.column_type === 'Metadata');
    const emptyLabel = metaColumns.find((m) => !m.metadata_label?.trim());
    if (emptyLabel) {
      setValidationError(`Metadata column "${emptyLabel.column_header}" needs a label.`);
      return;
    }

    // Check: no duplicate metadata labels
    const metaLabels = metaColumns.map((m) => m.metadata_label!.trim().toLowerCase());
    const seenLabels = new Set<string>();
    for (const label of metaLabels) {
      if (seenLabels.has(label)) {
        setValidationError(`Metadata label "${label}" is used on multiple columns. Each label must be unique.`);
        return;
      }
      seenLabels.add(label);
    }

    setValidationError(null);
    setShowPreview(true);
  };

  const getMappingSummary = () => {
    const identifiers = columnMappings.filter((m) => isIdentifierType(m.column_type));
    const aliases = identifiers.filter((m) => m.column_type !== productIdentifier);
    const metadata = columnMappings.filter((m) => m.column_type === 'Metadata');
    return {
      rowCount: csvRows.length,
      identifierTypeCount: identifiers.length,
      aliasTypeCount: aliases.length,
      metadataTypeCount: metadata.length,
    };
  };

  const handleImport = async () => {
    try {
      setImporting(true);

      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || null;

      // Find primary column index (first column mapped to the primary type)
      const primaryMapping = columnMappings.find(
        (m) => m.column_type === productIdentifier
      );
      if (!primaryMapping) return;
      const primaryColIndex = csvHeaders.indexOf(primaryMapping.column_header);

      // Build alias column indices (identifiers only, not metadata)
      // Additional columns of the primary type (beyond the first) become aliases
      const aliasColumns = columnMappings
        .filter((m) => isIdentifierType(m.column_type) && m !== primaryMapping)
        .map((m) => ({
          index: csvHeaders.indexOf(m.column_header),
          identifierType: m.column_type,
          header: m.column_header,
        }));

      // Build metadata column indices
      const metadataColumns = columnMappings
        .filter((m) => m.column_type === 'Metadata' && m.metadata_label?.trim())
        .map((m) => ({
          index: csvHeaders.indexOf(m.column_header),
          attributeName: m.metadata_label!.trim(),
        }));

      // Generate mappings with deduplication
      const dedupeSet = new Set<string>();
      const mappingsToInsert: Array<{
        organisation_id: string;
        file_id: string;
        primary_type: string;
        primary_value: string;
        alias_type: string;
        alias_value: string;
      }> = [];

      // Placeholder file_id — will be set after file insert
      const FILE_ID_PLACEHOLDER = '';

      for (const row of csvRows) {
        const primaryValue = row[primaryColIndex]?.trim() || '';
        if (!primaryValue) continue;

        for (const aliasCol of aliasColumns) {
          const aliasValue = row[aliasCol.index]?.trim() || '';
          if (!aliasValue) continue;
          if (primaryValue === aliasValue) continue;

          const dedupeKey = `${primaryValue}|${aliasCol.identifierType}|${aliasValue}`;
          if (dedupeSet.has(dedupeKey)) continue;
          dedupeSet.add(dedupeKey);

          mappingsToInsert.push({
            organisation_id: organisationId,
            file_id: FILE_ID_PLACEHOLDER,
            primary_type: productIdentifier,
            primary_value: primaryValue,
            alias_type: aliasCol.identifierType,
            alias_value: aliasValue,
          });
        }
      }

      // Generate metadata rows with deduplication
      const metaDedupeSet = new Set<string>();
      const metadataToInsert: Array<{
        organisation_id: string;
        file_id: string;
        primary_type: string;
        primary_value: string;
        attribute_name: string;
        attribute_value: string;
      }> = [];

      for (const row of csvRows) {
        const primaryValue = row[primaryColIndex]?.trim() || '';
        if (!primaryValue) continue;

        for (const metaCol of metadataColumns) {
          const value = row[metaCol.index]?.trim() || '';
          if (!value) continue;

          const dedupeKey = `${primaryValue}|${metaCol.attributeName}`;
          if (metaDedupeSet.has(dedupeKey)) continue;
          metaDedupeSet.add(dedupeKey);

          metadataToInsert.push({
            organisation_id: organisationId,
            file_id: '',
            primary_type: productIdentifier,
            primary_value: primaryValue,
            attribute_name: metaCol.attributeName,
            attribute_value: value,
          });
        }
      }

      // Insert file record
      const { data: fileData, error: fileError } = await fromAny('product_mapping_files')
        .insert({
          organisation_id: organisationId,
          filename: fileName,
          active_identifier_at_upload: productIdentifier,
          column_mappings: columnMappings.map((m) => ({
            column_header: m.column_header,
            column_type: m.column_type,
            ...(m.column_type === 'Metadata' && m.metadata_label ? { metadata_label: m.metadata_label } : {}),
          })),
          row_count: csvRows.length,
          created_by_email: userEmail,
        })
        .select('id')
        .single();

      if (fileError) throw fileError;
      const fileId = (fileData as { id: string }).id;

      // Set file_id on all mappings and metadata
      for (const m of mappingsToInsert) {
        m.file_id = fileId;
      }
      for (const m of metadataToInsert) {
        m.file_id = fileId;
      }

      // Batch insert identifier mappings in chunks of 100
      // For each batch: first expire any currently-active row for the same alias,
      // then insert the new row. This preserves history for point-in-time queries.
      const now = new Date().toISOString();
      const batchSize = 100;
      for (let i = 0; i < mappingsToInsert.length; i += batchSize) {
        const batch = mappingsToInsert.slice(i, i + batchSize);

        const aliasPairs = batch.map((m) => ({
          alias_type: m.alias_type,
          alias_value: m.alias_value,
        }));
        const supabaseAny = supabase as unknown as { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ error: unknown }> };
        const { error: expireError } = await supabaseAny.rpc('expire_active_product_mappings', {
          p_org_id: organisationId,
          p_alias_pairs: aliasPairs,
          p_expired_at: now,
          p_email: userEmail,
        });
        if (expireError) throw expireError;

        const batchWithTemporal = batch.map((m) => ({ ...m, effective_from: now, created_by_email: userEmail }));
        const { error: insertError } = await fromAny('product_mappings').insert(batchWithTemporal);
        if (insertError) throw insertError;
      }

      // Batch insert metadata in chunks of 100
      for (let i = 0; i < metadataToInsert.length; i += batchSize) {
        const batch = metadataToInsert.slice(i, i + batchSize);

        const { error: metaError } = await fromAny('product_metadata')
          .upsert(batch, { onConflict: 'organisation_id,primary_value,attribute_name' });

        if (metaError) throw metaError;
      }

      const parts = [];
      if (mappingsToInsert.length > 0) parts.push(`${mappingsToInsert.length} mappings`);
      if (metadataToInsert.length > 0) parts.push(`${metadataToInsert.length} metadata attributes`);

      toast({
        title: 'Import Successful',
        description: `Imported ${parts.join(' and ')} from ${fileName}.`,
      });

      queryClient.invalidateQueries({
        queryKey: ['product-mapping-files', organisationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['all-product-mappings', organisationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['product-metadata', organisationId],
      });

      resetState();
    } catch (error: unknown) {
      console.error('Error importing product mappings:', error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String((error as { message: unknown }).message)
            : 'Failed to import product mappings';
      toast({
        title: 'Import Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email || null;
    deactivateFileMutation.mutate({ fileId, organisationId, userEmail }, {
      onSuccess: () => {
        toast({ title: 'File deactivated', description: 'Mapping history has been preserved.' });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.message || 'Failed to deactivate file',
          variant: 'destructive',
        });
      },
    });
  };

  const handleEditMapping = (mapping: ProductMapping) => {
    setEditingMapping(mapping);
    setEditForm({
      primary_value: mapping.primary_value,
      primary_type: mapping.primary_type,
      alias_type: mapping.alias_type,
      alias_value: mapping.alias_value,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingMapping) return;
    setEditSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || null;
      await updateMappingMutation.mutateAsync({
        existingId: editingMapping.id,
        organisationId,
        fileId: editingMapping.file_id,
        primary_type: editForm.primary_type,
        primary_value: editForm.primary_value.trim(),
        alias_type: editForm.alias_type,
        alias_value: editForm.alias_value.trim(),
        userEmail,
      });
      toast({ title: 'Mapping updated', description: 'Previous version preserved in history.' });
      setEditingMapping(null);
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update mapping',
        variant: 'destructive',
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleSoftDeleteMapping = async (mapping: ProductMapping) => {
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email || null;
    softDeleteMappingMutation.mutate({ mappingId: mapping.id, organisationId, userEmail }, {
      onSuccess: () => {
        toast({ title: 'Mapping removed', description: 'History has been preserved.' });
      },
      onError: (error) => {
        toast({ title: 'Error', description: error.message || 'Failed to remove mapping', variant: 'destructive' });
      },
    });
  };

  const summary = showPreview ? getMappingSummary() : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-2xl font-bold">Product Mappings</h2>
        <p className="text-muted-foreground">
          Map product identifiers across different systems. Mapped identifiers will be
          merged in reports, inventory, and NERV calculations.
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Product Mappings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="product-csv-upload">
              Upload a CSV file with columns for each identifier type (e.g.{' '}
              <code className="bg-muted px-1 py-0.5 rounded text-sm">SKU</code>,{' '}
              <code className="bg-muted px-1 py-0.5 rounded text-sm">UPC</code>, etc.).
            </Label>
            <Input
              id="product-csv-upload"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="cursor-pointer"
            />
          </div>

          {parseError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{parseError}</p>
            </div>
          )}

          {/* Column Mapping Step */}
          {csvHeaders.length > 0 && !showPreview && (
            <div className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Column Mapping</h3>
                <p className="text-xs text-muted-foreground">
                  Map each CSV column to an identifier type or metadata. Your primary identifier is{' '}
                  <strong>{productIdentifier}</strong>.
                </p>
              </div>

              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>CSV Column</TableHead>
                      <TableHead>Column Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {columnMappings.map((mapping, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm">
                          {mapping.column_header}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={mapping.column_type}
                              onValueChange={(value: string) =>
                                handleColumnMappingChange(idx, value as ColumnType)
                              }
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Identifiers</SelectLabel>
                                  {IDENTIFIER_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {type}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                <SelectSeparator />
                                <SelectItem value="Metadata">Metadata</SelectItem>
                                <SelectSeparator />
                                <SelectItem value="Ignore">Ignore</SelectItem>
                              </SelectContent>
                            </Select>
                            {mapping.column_type === productIdentifier &&
                              columnMappings.filter((m) => m.column_type === productIdentifier).indexOf(mapping) === 0 &&
                              columnMappings.filter((m) => m.column_type === productIdentifier).length > 1 && (
                              <Badge variant="secondary" className="text-xs">primary</Badge>
                            )}
                            {mapping.column_type === 'Metadata' && (
                              <Input
                                value={mapping.metadata_label || ''}
                                onChange={(e) => handleMetadataLabelChange(idx, e.target.value)}
                                placeholder="Label (e.g. Description)"
                                className="w-48"
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {validationError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{validationError}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={validateAndPreview}>
                  <FileUp className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <Button variant="outline" onClick={resetState}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Preview Step */}
          {showPreview && summary && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <CheckCircle className="h-5 w-5 text-blue-500 shrink-0" />
                <p className="text-sm text-blue-700">
                  Importing <strong>{summary.rowCount}</strong> rows with{' '}
                  <strong>{summary.identifierTypeCount}</strong> identifier types
                  {summary.metadataTypeCount > 0 && (
                    <>
                      {' '}and <strong>{summary.metadataTypeCount}</strong> metadata fields
                    </>
                  )}
                  {' '}from <strong>{fileName}</strong>
                </p>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleImport} disabled={importing}>
                  <FileUp className="h-4 w-4 mr-2" />
                  {importing ? 'Importing...' : 'Confirm Import'}
                </Button>
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button variant="outline" onClick={resetState}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Uploaded Files Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Uploaded Files ({files?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filesLoading ? (
            <p className="text-muted-foreground text-sm">Loading files...</p>
          ) : !files || files.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">
              No product mapping files uploaded. Upload a CSV to get started.
            </p>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Rows</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => {
                    const activeIdentifier = file.active_identifier_at_upload;
                    const filename = file.filename;
                    const rowCount = file.row_count;
                    const isStale = activeIdentifier !== productIdentifier;

                    return (
                      <TableRow key={file.id}>
                        <TableCell className="font-mono text-sm">{filename}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">
                              Uploaded as {activeIdentifier} mappings
                            </span>
                            {isStale && (
                              <Badge variant="warning">
                                Uploaded when primary was {activeIdentifier}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{rowCount}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(file.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Deactivate file"
                            disabled={deactivateFileMutation.isPending}
                            onClick={() => setFileToDeactivate({ id: file.id, filename })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Mappings Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Current Mappings ({allMappings?.length ?? 0})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mappingsLoading ? (
            <p className="text-muted-foreground text-sm">Loading mappings...</p>
          ) : !allMappings || allMappings.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">
              No mappings yet. Upload a CSV above to create product mappings.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by primary or alias value..."
                  value={mappingSearch}
                  onChange={(e) => setMappingSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {filteredMappings.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No mappings match your search.
                </p>
              ) : (
                <div className="border rounded-md max-h-[500px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Primary Value</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Alias Value</TableHead>
                        <TableHead>Alias Type</TableHead>
                        <TableHead>Source File</TableHead>
                        <TableHead className="w-28"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell className="font-mono text-sm font-medium">
                            {mapping.primary_value}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {mapping.primary_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {mapping.alias_value}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {mapping.alias_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {fileNameMap.get(mapping.file_id) || '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Edit mapping"
                                onClick={() => handleEditMapping(mapping)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View history"
                                onClick={() => setHistoryMapping({ aliasType: mapping.alias_type, aliasValue: mapping.alias_value })}
                              >
                                <Clock className="h-3.5 w-3.5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Remove mapping"
                                    disabled={softDeleteMappingMutation.isPending}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove mapping?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will remove the mapping from <strong>{mapping.alias_value}</strong> ({mapping.alias_type}) → <strong>{mapping.primary_value}</strong>. History is preserved — past reports will not be affected.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleSoftDeleteMapping(mapping)}>
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Showing {filteredMappings.length} of {allMappings.length} mappings
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* File Deactivation Confirmation */}
      <AlertDialog open={!!fileToDeactivate} onOpenChange={(open) => { if (!open) setFileToDeactivate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate mapping file?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate &quot;{fileToDeactivate?.filename}&quot; and remove its
              active mappings. History is preserved — past reports will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (fileToDeactivate) handleDeleteFile(fileToDeactivate.id);
              setFileToDeactivate(null);
            }}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Mapping Dialog */}
      <Dialog open={!!editingMapping} onOpenChange={(open) => { if (!open) setEditingMapping(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Primary Type</Label>
                <Select
                  value={editForm.primary_type}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, primary_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IDENTIFIER_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Primary Value</Label>
                <Input
                  value={editForm.primary_value}
                  onChange={(e) => setEditForm((f) => ({ ...f, primary_value: e.target.value }))}
                  placeholder="e.g. SKU-001"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Alias Type</Label>
                <Select
                  value={editForm.alias_type}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, alias_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IDENTIFIER_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Alias Value</Label>
                <Input
                  value={editForm.alias_value}
                  onChange={(e) => setEditForm((f) => ({ ...f, alias_value: e.target.value }))}
                  placeholder="e.g. 012345678901"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMapping(null)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={
                editSaving ||
                !editForm.primary_value.trim() ||
                !editForm.alias_value.trim()
              }
            >
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping History Dialog */}
      <Dialog open={!!historyMapping} onOpenChange={(open) => { if (!open) setHistoryMapping(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Mapping History
              {historyMapping && (
                <span className="ml-2 font-normal text-muted-foreground text-sm">
                  {historyMapping.aliasType}: {historyMapping.aliasValue}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading history...</p>
          ) : !historyRows || historyRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No history found.</p>
          ) : (
            <div className="border rounded-md overflow-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Effective To</TableHead>
                    <TableHead>Primary Value</TableHead>
                    <TableHead>Modified By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyRows.map((row, idx) => (
                    <TableRow key={row.id} className={row.effective_to ? 'text-muted-foreground' : ''}>
                      <TableCell className="text-xs">{historyRows.length - idx}</TableCell>
                      <TableCell className="text-xs">
                        {new Date(row.effective_from).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.effective_to ? (
                          new Date(row.effective_to).toLocaleString()
                        ) : (
                          <Badge variant="secondary" className="text-xs">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <span>{row.primary_value}</span>
                        <span className="text-muted-foreground ml-1">({row.primary_type})</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.modified_by_email || row.created_at && '(upload)'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
